import { useState, useEffect, useCallback, useRef } from 'react';

interface ImageLoaderState {
  isLoading: boolean;
  isLoaded: boolean;
  hasError: boolean;
  error?: string;
}

interface UseImageLoaderOptions {
  onLoad?: () => void;
  onError?: (error: string) => void;
  preload?: boolean;
  retryCount?: number;
  retryDelay?: number;
}

export const useImageLoader = (
  src: string | undefined,
  options: UseImageLoaderOptions = {}
): ImageLoaderState => {
  const [state, setState] = useState<ImageLoaderState>({
    isLoading: false,
    isLoaded: false,
    hasError: false,
  });

  const { onLoad, onError, preload = true, retryCount = 2, retryDelay = 1000 } = options;
  const currentSrcRef = useRef<string | undefined>();
  const retryTimeoutRef = useRef<NodeJS.Timeout>();
  const imgRef = useRef<HTMLImageElement>();

  const loadImage = useCallback(() => {
    if (!src) {
      setState({
        isLoading: false,
        isLoaded: false,
        hasError: false,
      });
      return;
    }

    // Clear any existing retry timeout
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }

    // Clean up previous image
    if (imgRef.current) {
      imgRef.current.onload = null;
      imgRef.current.onerror = null;
    }

    currentSrcRef.current = src;
    setState(prev => ({ ...prev, isLoading: true, hasError: false }));

    const img = new Image();
    imgRef.current = img;
    
    let retries = 0;

    const attemptLoad = () => {
      if (currentSrcRef.current !== src) {
        // Source changed, abort this load
        return;
      }

      img.onload = () => {
        if (currentSrcRef.current !== src) {
          // Source changed, ignore this load
          return;
        }
        
        setState({
          isLoading: false,
          isLoaded: true,
          hasError: false,
        });
        onLoad?.();
      };

      img.onerror = () => {
        if (currentSrcRef.current !== src) {
          // Source changed, ignore this error
          return;
        }

        const error = `Failed to load image: ${src}`;
        
        if (retries < retryCount) {
          // Retry loading
          retries++;
          retryTimeoutRef.current = setTimeout(() => {
            if (currentSrcRef.current === src) {
              attemptLoad();
            }
          }, retryDelay);
        } else {
          // Max retries reached
          setState({
            isLoading: false,
            isLoaded: false,
            hasError: true,
            error,
          });
          onError?.(error);
        }
      };

      // Only add crossOrigin if explicitly needed (causes CORS issues with some CDNs)
      // img.crossOrigin = 'anonymous';
      img.src = src;
    };

    attemptLoad();
  }, [src, onLoad, onError, retryCount, retryDelay]);

  useEffect(() => {
    if (preload) {
      loadImage();
    }
  }, [loadImage, preload]);

  // Cleanup on unmount or src change
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (imgRef.current) {
        imgRef.current.onload = null;
        imgRef.current.onerror = null;
      }
    };
  }, [src]);

  return state;
};

export const useImagePreloader = (imageUrls: string[]) => {
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const [loadingImages, setLoadingImages] = useState<Set<string>>(new Set());
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const imgRefs = useRef<Map<string, HTMLImageElement>>(new Map());

  const preloadImage = useCallback((url: string) => {
    if (loadedImages.has(url) || loadingImages.has(url) || failedImages.has(url)) {
      return;
    }

    setLoadingImages(prev => new Set(prev).add(url));

    const img = new Image();
    // Only add crossOrigin if explicitly needed (causes CORS issues with some CDNs)
    // img.crossOrigin = 'anonymous';
    imgRefs.current.set(url, img);
    
    img.onload = () => {
      setLoadedImages(prev => new Set(prev).add(url));
      setLoadingImages(prev => {
        const newSet = new Set(prev);
        newSet.delete(url);
        return newSet;
      });
      imgRefs.current.delete(url);
    };

    img.onerror = () => {
      setFailedImages(prev => new Set(prev).add(url));
      setLoadingImages(prev => {
        const newSet = new Set(prev);
        newSet.delete(url);
        return newSet;
      });
      imgRefs.current.delete(url);
    };

    img.src = url;
  }, [loadedImages, loadingImages, failedImages]);

  useEffect(() => {
    imageUrls.forEach(url => {
      if (url) {
        preloadImage(url);
      }
    });

    // Cleanup on unmount
    return () => {
      imgRefs.current.forEach(img => {
        img.onload = null;
        img.onerror = null;
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
      imgRefs.current.clear();
    };
  }, [imageUrls, preloadImage]);

  return {
    loadedImages,
    loadingImages,
    failedImages,
    isImageLoaded: (url: string) => loadedImages.has(url),
    isImageLoading: (url: string) => loadingImages.has(url),
    isImageFailed: (url: string) => failedImages.has(url),
  };
}; 